import { FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import {
  ApiPattern,
  IApiPattern,
} from 'src/app/models_new/classes/api-models/ApiPattern';
import {
  ApiScene,
  IApiScene,
} from 'src/app/models_new/classes/api-models/ApiScene';
import {
  ApiStrategy,
  IApiStrategy,
} from 'src/app/models_new/classes/api-models/ApiStrategy';
import { LocalStorageKey } from 'src/app/models_new/enums/local-storage-keys';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { IZimWizNavItem, NavItemsConfig } from './nav-items-config';
import { ActivatedRoute, Router } from '@angular/router';
import { delay } from 'rxjs';

export interface IZimWizardState {
  dualProductMode: boolean;
  patterns: ApiPattern[];
  hardwares: ApiScene[];
  softwares: ApiStrategy[];
  softwarePresets: ApiStrategy[];
}

export class ZimWizard {
  patterns: ApiPattern[];
  hardwares: ApiScene[];
  softwares: ApiStrategy[];
  softwarePresets: ApiStrategy[];
  navItemsConfig: IZimWizNavItem[] = NavItemsConfig;
  currentPage: IZimWizNavItem = this.navItemsConfig[0];

  formBuilder = new UntypedFormBuilder();
  formGroup = this.formBuilder.group({
    p: [[], Validators.required],
    hw: [[], Validators.required],
    sw: [[], Validators.required],
  });

  dualProductMode: FormControl<boolean> = new FormControl();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStore: LocalStorageService,
    patterns: IApiPattern[],
    hardware: IApiScene[],
    software: IApiStrategy[],
    softwarePresets: IApiStrategy[],
    dualProductMode?: boolean
  ) {
    this.patterns = patterns.map((m) => new ApiPattern(m));
    this.hardwares = hardware.map((m) => new ApiScene(m));
    this.softwares = software.map((m) => new ApiStrategy(m));
    this.softwarePresets = softwarePresets.map((m) => new ApiStrategy(m));

    this.formGroup.get('p').setValue(patterns);
    this.formGroup.get('hw').setValue(hardware);
    this.updateSoftwareFormGroup();

    if (this.route.snapshot.queryParams.step) {
      if (+this.route.snapshot.queryParams.step > 2) {
        this.selectStepperIndex(+this.route.snapshot.queryParams.step - 1);
      } else {
        this.selectStepperIndex(+this.route.snapshot.queryParams.step);
      }
    }

    this.enableDualProductMode(dualProductMode);
    this.dualProductMode.valueChanges.pipe(delay(0)).subscribe((enabled) => {
      // Update selected patterns and hardwares to match dual product mode state.
      if (enabled) {
        this.patterns = this.patterns.slice(0, 2);
        this.hardwares = (this.hardwares as any[]).filter(
          (row: { hardware: ApiScene }) => {
            return row.hardware.data.conveyors.length === 2;
          }
        );
      }
      this.storeData();
    });
  }

  public getSelected(
    type: 'patterns' | 'hardwares' | 'softwares' | 'softwarePresets',
    index?: number
  ) {
    return index !== undefined ? [this[type][index]] : this[type];
  }

  private selectPage(id: number) {
    this.currentPage = this.navItemsConfig.find((f) => f.id === id);
  }

  private updateQueryParams(stepId: number) {
    if (stepId !== this.route.snapshot.queryParams.step) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { step: stepId },
      });
    }
  }

  public selectStepperIndex(stepId: number) {
    const redo = (stepId) => {
      this.selectStepperIndex(stepId - 1);
    };

    if (stepId === 1 && !this.patterns.length) {
      redo(stepId);
    } else if (
      stepId === 2 &&
      (!this.hardwares.length || !this.patterns.length)
    ) {
      redo(stepId);
    } else if (
      stepId === 3 &&
      ((!this.softwares.length && !this.softwarePresets.length) ||
        !this.hardwares.length ||
        !this.patterns.length)
    ) {
      redo(stepId);
    } else {
      this.updateQueryParams(stepId);
      this.selectPage(stepId);
    }
  }

  public editPatterns(patterns: ApiPattern[], index?: number) {
    if (this.dualProductMode && index !== undefined) {
      this.patterns[index] = patterns[0];
    } else {
      this.patterns = patterns;
    }
    this.formGroup.get('p').setValue(this.patterns);
    this.storeData();
  }

  public editHardwares(hws: ApiScene[]) {
    this.hardwares = hws;
    this.formGroup.get('hw').setValue(hws);
    this.storeData();
  }

  private updateSoftwareFormGroup() {
    const sws = this.softwares.concat(this.softwarePresets);
    this.formGroup.get('sw').setValue(sws);
  }

  public editSoftwares(sws: ApiStrategy[]) {
    this.softwares = sws;
    this.updateSoftwareFormGroup();
    this.storeData();
  }

  public editSoftwarePresets(sws: ApiStrategy[]) {
    this.softwarePresets = sws;
    this.updateSoftwareFormGroup();
    this.storeData();
  }

  public getData() {
    return this;
  }

  public enableDualProductMode(enabled: boolean) {
    this.dualProductMode.setValue(enabled);
  }

  private storeData() {
    const storeThis: IZimWizardState = {
      dualProductMode: this.dualProductMode.value,
      patterns: this.patterns,
      hardwares: this.hardwares,
      softwares: this.softwares,
      softwarePresets: this.softwarePresets,
    };
    this.localStore.setData(LocalStorageKey.SIMULATION_WIZARD_STATE, storeThis);
  }

  public clearData() {
    this.patterns = [];
    this.hardwares = [];
    this.softwares = [];
    this.softwarePresets = [];
    this.formGroup.reset();
  }
}
